import { Flex } from "@chakra-ui/react";
import { Typography } from "@kelp-dao/kernel-design-system";
import VaultHeader from "@components/V2/Vault/Common/VaultHeader";
import { useHgEthVault, useHgEthDeposit, useHgEthWithdraw } from "./hgEthHooks";
import VaultDWB from "@components/V2/Vault/Common/VaultDWB";
import Portfolio from "@components/V2/Vault/Common/Portfolio";
import DefiPartnerships from "@components/V2/Vault/Common/DefiPartnerships";
import { DepositProps } from "@components/V2/Vault/Common/Deposit";
import { WithdrawProps } from "@components/V2/Vault/Common/Withdraw";
import { LSTIconMapping } from "@constants/common";
import useHgEthClaims from "./useHgEthClaims";
import Tooltip from "@components/Tooltip";

const HighGrowthVault = () => {
  const { vaultHeaderData, portfolioConfig, isSanctioned, constants } =
    useHgEthVault();

  const depositLogic = useHgEthDeposit();
  const withdrawLogic = useHgEthWithdraw();

  // Convert legacy logic to new simple props
  const depositProps: DepositProps = {
    vaultTokenSymbol: "hgETH",
    inputTokenSymbol: (depositLogic as any)?.selectedERC20Vault || "rsETH",
    inputTokenIcon:
      LSTIconMapping[(depositLogic as any)?.selectedERC20Vault || "rsETH"] ||
      LSTIconMapping["rsETH"],
    vaultTokenIcon: LSTIconMapping["hgETH"],
    inputTokenBalance: (depositLogic as any)?.tokenBalance || 0,
    vaultTokenBalance: (depositLogic as any)?.xTokenVaultBalance || 0,
    convertedAmount: (depositLogic as any)?.convertedAmount || "0",
    exchangeRateDisplay: (depositLogic as any)?.valueInEth
      ? `1 hgETH = ${(depositLogic as any).valueInEth} ETH`
      : undefined,
    isConnected: (depositLogic as any)?.isConnected || false,
    isApproved: (depositLogic as any)?.isApproved || false,
    isSupportedChain:
      (depositLogic as any)?.isYieldVaultSupportedChain !== false,
    onDeposit: (amount?: string) =>
      (depositLogic as any)?.handleClick?.(amount),
    onApprove: (amount?: string) =>
      (depositLogic as any)?.handleClick?.(amount),
    onAmountChange: (depositLogic as any)?.handleChange,
    onTokenSelect: (depositLogic as any)?.handleTokenSelect,
    checkInputConstraints: (depositLogic as any)?.checkInput,
    vaultType: "highGrowth",
    disclaimer: <></>,
  };

  const withdrawProps: WithdrawProps = {
    vaultTokenSymbol: "hgETH",
    outputTokenSymbol: "rsETH",
    vaultTokenIcon: LSTIconMapping["hgETH"],
    outputTokenIcon: LSTIconMapping["rsETH"],
    vaultTokenBalance: (withdrawLogic as any)?.tokenBalance || 0,
    outputTokenBalance: (withdrawLogic as any)?.xTokenTokenBalance || 0,
    convertedAmount: (withdrawLogic as any)?.convertedAmount || "0",
    exchangeRateDisplay: (withdrawLogic as any)?.valueInEth
      ? `1 hgETH = ${(withdrawLogic as any).valueInEth} ETH`
      : undefined,
    isConnected: (withdrawLogic as any)?.isConnected || false,
    isApproved: (withdrawLogic as any)?.isApproved || false,
    isSupportedChain:
      (withdrawLogic as any)?.isYieldVaultSupportedChain !== false,
    onWithdraw: (amount?: string) =>
      (withdrawLogic as any)?.handleClick?.(amount),
    onApprove: (amount?: string) =>
      (withdrawLogic as any)?.handleClick?.(amount),
    onAmountChange: (withdrawLogic as any)?.handleChange,
    checkInputConstraints: (withdrawLogic as any)?.checkInput,
    vaultType: "highGrowth",
    disclaimer: (
      <>
        The withdrawal amount will be <strong>auto-claimed</strong> to your
        address in 3-4 days once you request a withdrawal. Check pending claims
        in the ‘Claims’ section. While in the withdrawal queue, you will not
        earn any position rewards or points. All your withdrawals in a day will
        be fulfilled as a single transaction.
      </>
    ),
  };

  return (
    <>
      <Flex
        direction={"column"}
        alignItems={"center"}
        gap="20px"
        // p="16px"
        maxW="1279px"
        w="100%"
        mx="auto"
        pointerEvents={isSanctioned ? "none" : "auto"}
        opacity={isSanctioned ? 0.5 : 1}
      >
        <VaultHeader
          isLoadingAPYData={vaultHeaderData.isLoadingAPYData}
          isLoadingTotalStaked={vaultHeaderData.isLoadingTotalStaked}
          totalStaked={vaultHeaderData.totalStaked}
          totalStakedEth={vaultHeaderData.totalStakedEth}
          apyValue={vaultHeaderData.apyValue}
          apyLabel={vaultHeaderData.apyLabel}
          totalStakedLabel={vaultHeaderData.totalStakedLabel}
          title={vaultHeaderData.title}
          logo={vaultHeaderData.logo}
          tooltipComponent={
            <Tooltip
              placement="top"
              label={
                <Typography
                  type="body"
                  variant="xsmall"
                  className="font-normal text-xs text-text-primary"
                >
                  7 day trailing APR <br />
                  Check <strong>&apos;Strategy Reports&apos;</strong> section
                  for details
                </Typography>
              }
            />
          }
          auditedUrl={vaultHeaderData.auditedUrl}
        />

        <VaultDWB
          availableTabs={["deposit", "withdraw"]}
          tabLabels={{
            deposit: "Deposit",
            withdraw: "Withdraw",
          }}
          initialTab="deposit"
          depositProps={depositProps}
          withdrawProps={withdrawProps}
        />

        <Portfolio
          config={portfolioConfig}
          constants={constants}
          vaultType="highGrowth"
          xTokenSymbol="hgETH"
          asset="hgeth"
          useClaimsHook={useHgEthClaims}
          aprTooltip="Realised APR post fee"
        />

        <DefiPartnerships asset="hgeth" />
      </Flex>
    </>
  );
};

export default HighGrowthVault;
